import React, { createContext, useContext, useEffect, useState } from "react"
import * as Sentry from "@sentry/browser"
import { navigate } from "gatsby"
import { isObjectEmpty } from "../utilities/checkValidity"
import { checkURLParams } from "../utilities/checkURLParams"
import { UseShopifyDataQuery } from "../hooks/shopifyData"
import { convertVariantIdToLegacyResourceId, convertVariantIdToStorefrontId } from "../utilities/convertIdtoLegacyId"
import { compareDates, useCountdown } from "../components/Countdown/hooks/useCountdown"

import FULL_PRODUCT_DATA from "../../static/data/full-product"
import { formatPrice } from "../utilities/formatProductInfo"

const isBrowser = typeof window !== "undefined"

const defaultState = {
  addVariantToCart: (
    variantId,
    productId,
    quantity,
    price,
    sellingPlan,
    title,
    type,
    options,
    currentCartItem,
    isCart,
  ) => {},
  updateItemInCart: (
    variantId,
    productId,
    quantity,
    selling_plan,
    price,
    isSidebar,
    currentCartItem,
    options,
    isCart,
    error,
  ) => {},
  updateQuantity: (cartItem, quantity) => {},
  removeLineItemInCart: cartItem => {},
  sidebarOpen: false,
  handleSidebar: () => {},
  checkoutUrl: "",
  total: 0,
  subtotal: 0,
  cart: null,
  country: null,
  shipping: true,
  updateCountry: newCountry => {},
  changeModalCountry: false,
  handleChangeModalCountry: isPopup => {},
  pageLoader: true,
  discount: 0,
  error: { status: "", message: "" },
  shopifyData: [],
  giftId: "",
  showGiftBox: false,
  handleGiftBox: () => {},
  isDiscountActive: false,
  discountAppliedToBundles: false,
  discountCode: "",
  discountThroughText: "",
  discountPercentage: "",
  startDateTimeBasedDiscount: "",
  endDateTimeBasedDiscount: "",
  isPromo: false,
}
const ThemeContext = createContext(defaultState)

export const useTheme = () => {
  return useContext(ThemeContext)
}

const ThemeProvider = ({ children }) => {
  const shopifyData = UseShopifyDataQuery()

  const [cart, setCart] = useState(null)
  const [checkoutUrl, setCheckoutUrl] = useState("")

  const [total, setTotal] = useState(0)
  const [subtotal, setSubtotal] = useState(0)

  const [country, setCountry] = useState("US")
  const [shipping, setShipping] = useState(true)
  const [changeModalCountry, setChangeModalCountry] = useState(false)
  const [pageLoader, setPageLoader] = useState(true)

  useEffect(() => {
    const cookies = document.cookie.split("; ").reduce((acc, item) => {
      const [key, value] = item.split("=")
      acc[key] = value
      return acc
    }, {})

    const override = cookies.storeOverride === "true"
    const localCountry = localStorage.getItem("country") || "US"
    const localShipping = localStorage.getItem("shipping") === "true"

    // Prefer override/localStorage if user has made a choice
    if (override || localCountry) {
      setCountry(["US", "CA", "GB"].includes(localCountry) ? localCountry : "worldwide")
      setShipping(localShipping)
    } else if (cookies.country) {
      // Else use the country from the edge cookie
      const edgeCountry = cookies.country
      const shippingAllowed = ["US", "CA", "GB"].includes(edgeCountry)

      setCountry(["US", "CA", "GB"].includes(edgeCountry) ? edgeCountry : "worldwide")
      setShipping(shippingAllowed)
      localStorage.setItem("country", edgeCountry)
      localStorage.setItem("shipping", shippingAllowed.toString())
    }
  }, [])

  // Gift id - gift box on cart
  const giftId = "gid://shopify/Product/7374601781440" // All or Something Crop Top
  const [showGiftBox, setShowGiftBox] = useState(false)

  // NEW OFFERS - individual pages -
  // NOTE: UPDATE CODE FOR NEW OFFER
  const offerId = [
    "gid://shopify/Product/7729718329536", // Home Workout Domination Protein Bundle
    "gid://shopify/Product/7754890674368", // Body Fuel System Protein Bundle
    "gid://shopify/Product/7874233270464", // Booty and Abs Challenge Protein Bundle
    "gid://shopify/Product/7749101551808", // 30 Day Meal Plan Protein Bundle
    "gid://shopify/Product/7891449905344", // My Dinner Plan Protein Bundle
    "gid://shopify/Product/7751589953728", // 90 Day Challenge Protein Bundle
    "gid://shopify/Product/7751590510784", // Lioness Protein Bundle
  ]

  // UPDATE ARRAY WITH PRODUCT ID TO REMOVE ITEM FROM LOCAL STORAGE
  const hiddenProductsFromLS = [
    "gid://shopify/Product/7492219207872", // Muscle Tank
    "gid://shopify/Product/7374623080640", // Rock Your Life Muscle Tank
    "gid://shopify/Product/7374635368640", // Stop, Drop and Betty Rock Muscle Tank
    "gid://shopify/Product/7375209332928", // All or Something Muscle Tank
    "gid://shopify/Product/7374596407488", // Stop Drop and Betty Rock Crop Top
    "gid://shopify/Product/7374599618752", // Rock Your Life Crop Top
    "gid://shopify/Product/7374582513856", // Rockstar Crop Top
  ]

  /** -----------------------------------------
   Page Loader - check data
   ------------------------------------------- */
  useEffect(() => {
    setPageLoader(!(shopifyData.length > 0))
  }, [shopifyData])

  /** -----------------------------------------
   DISCOUNT
    - Discount with discount code
    - Discount that is time based
    - Discount with discounted price
   ------------------------------------------- */
  const [isDiscountActive, setIsDiscountActive] = useState(false)
  const [discount, setDiscount] = useState(0)

  // DISCOUNT CODE VALUE
  const discountCode = "SUMMER20"
  // DISCOUNT TEXT VALUE
  const discountThroughText = "THU 6/18"
  // DISCOUNT PERCENTAGE
  const discountPercentage = "20% OFF"

  // FOR FULL PRICE DISCOUNT visible on cart (product id)
  const discountProductPriceId = ""
  // FOR SPECIFIC PRODUCT DISCOUNTED WITH CODE
  const discountedProductId = "" // All Supplements
  // FOR SUPPLEMENTS/BUNDLES DISCOUNT
  const discountAppliedToSupplements = true
  const discountAppliedToBundles = true

  // TIME BASED DISCOUNT
  const startDateTimeBasedDiscount = "06/16/2026 00:00:00" // MM/DD/YYYY HH:mm:ss - Add the start date of the discount
  const endDateTimeBasedDiscount = "06/18/2026 23:59:59" // MM/DD/YYYY HH:mm:ss - Add the end date of the countdown
  // DISCOUNT REMOVE DATE
  const removeDiscountDate = "06/18/2026 23:59:59" // MM/DD/YYYY HH:mm:ss - Add the time when the discount components should hide

  // IS PROMO ACTIVE
  const isPromoActive = false // Set this so true/false if banners/product box changes are needed with the promo
  const [isPromo, setIsPromo] = useState(isPromoActive)

  // Time left until the removeDiscountDate is reached
  const discountTimeLeft = useCountdown(removeDiscountDate)

  // Activate/Remove discount based on entered time
  useEffect(() => {
    // If development compare with current start date, if it's in the future, show the discount
    if (process.env.GATSBY_NODE_ENV === "development" && !discountTimeLeft.every(item => item === 0)) {
      setIsDiscountActive(true)
      if (isPromoActive) {
        setIsPromo(true)
      }
      return
    }

    if (discountTimeLeft.every(item => item === 0)) {
      setIsDiscountActive(false)
      if (isPromoActive) {
        setIsPromo(false)
      }
    } else {
      setIsDiscountActive(compareDates(new Date(), startDateTimeBasedDiscount, removeDiscountDate))
      if (isPromoActive) {
        setIsPromo(true)
      }
    }
  }, [discountTimeLeft])

  /** -----------------------------------------
   CART
    - Set up cart on page load
   ------------------------------------------- */
  // Get the cartItems from localStorage on load, if they exist
  useEffect(() => {
    if (window.location.href.indexOf("https://store.thebettyrocker.com/checkout") !== -1) {
      window.location.href = "https://store.thebettyrocker.com/cart/"
    }

    if (!shopifyData.length) return

    const parameters = checkURLParams()
    let cartLS = JSON.parse(localStorage.getItem("cartItems"))

    if (parameters.hasOwnProperty("cart")) {
      if (parameters["cart"] === "clear") {
        localStorage.removeItem("cartItems")
        setCart([])
      }
    }

    if (parameters.hasOwnProperty("status")) {
      checkCartItemsQuantityStock(parameters["status"], cartLS)
    }

    // Sync local storage across multiple tabs
    window.addEventListener("storage", function (event) {
      if (event.key === "cartItems") {
        cartLS = event.storageArea.cartItems
        setCart(cartLS ? JSON.parse(cartLS) : [])
      }

      if (event.key === "country") {
        updateCountry(event.newValue, true)
      }
    })

    let cartLSFinal = cartLS ? cartLS : []

    if (cartLS) {
      cartLS.forEach((cartItem, index) => {
        cartItem.id = index

        const shopifyProduct = shopifyData.filter(item => item.shopifyId === cartItem.productId)
        if (shopifyProduct.length) {
          // Product has been removed and it should be hidden from cart
          const isProductHidden = hiddenProductsFromLS.includes(cartItem.productId)

          if (!isProductHidden) {
            shopifyProduct[0].variants.forEach(variant => {
              // get variant id
              const variantId = variant.shopifyId

              if (variantId === cartItem.variantId) {
                if (!variant.availableForSale) {
                  let newCart = [...cartLS]
                  newCart.forEach(variant => {
                    if (variant.variantId === variantId && variant.selling_plan === cartItem.selling_plan) {
                      cartLSFinal = removeLSItem(newCart, cartItem)
                    }
                  })
                  localStorage.setItem("cartItems", JSON.stringify(newCart))
                }
              }
            })
          } else {
            // If the product is in shopify but has been hidden on the store, remove it from local storage
            let newCart = [...cartLSFinal]
            cartLSFinal = removeLSItem(newCart, cartItem)
          }
        } else {
          // If there is a product in the cart that has been removed from shopify, remove it from local storage
          let newCart = [...cartLSFinal]
          cartLSFinal = removeLSItem(newCart, cartItem)
        }
      })
    }

    // If the gift hasn't been declined, show the gift box on cart
    let declinedGift = getCookie("declined-gift")
    setShowGiftBox(!declinedGift && giftId.length)

    // Set Cart
    setCart(cartLSFinal)
  }, [shopifyData])

  /** -----------------------------------------
   SHOPIFY URL
   - Set shopify api url
   -  Calculate Total and Subtotal in Order Summary
   ------------------------------------------- */

  // Set url
  useEffect(() => {
    if (!cart || !cart.length) return

    const storeUrl = "https://checkout.thebettyrocker.com/cart/clear?return_to="
    let returnToUrl = "/cart/add?"
    let checkoutUrl = "&return_to=/checkout"
    let apiUrl = ""
    let id

    let discountUrl = ""

    cart.forEach((item, index) => {
      // Find the product and replace the id with the StorefrontId until shopify makes changes in POST add.js and recognizes the nww id
      id = convertVariantIdToLegacyResourceId(shopifyData, item.variantId)
      returnToUrl += `${index > 0 ? "&" : ""}items[][id]=${id}&items[][quantity]=${item.quantity}`

      if (item.selling_plan && item.selling_plan !== "No") {
        returnToUrl += `&items[][selling_plan]=${item.selling_plan}`
      }
    })
    if (isDiscountActive) {
      let isOneTimeSupplementPurchased = false

      // CHECK IF DISCOUNT CONDITION IS MET
      let discountProductTypeSelected

      if (discountedProductId.length) {
        // DISCOUNT APPLIED IF A SPECIFIC SUPPLEMENT OR BUNDLE IS PURCHASED
        discountProductTypeSelected = cart.filter(
          cartItem => cartItem.type === "supplements" && cartItem.productId === discountedProductId,
        )
      } else {
        // DISCOUNT APPLIED IF SUPPLEMENTS ARE PURCHASED
        // Check if there is a supplement purchased
        if (discountAppliedToSupplements && !discountAppliedToBundles) {
          discountProductTypeSelected = cart.filter(cartItem => cartItem.type === "supplements")
        }

        // DISCOUNT APPLIED IF SUPPLEMENT OR BUNDLE IS PURCHASED
        // Check if there is supplement or bundle purchased
        if (discountAppliedToSupplements && discountAppliedToBundles) {
          discountProductTypeSelected = cart.filter(
            cartItem => cartItem.type === "supplements" || cartItem.type === "bundles",
          )
        }
      }

      if (discountProductTypeSelected.length) {
        // Check if there is a one time supplement purchased
        isOneTimeSupplementPurchased = discountProductTypeSelected.some(
          product => product.selling_plan.toString().toLowerCase() === "no",
        )

        // Update url with discount code if it's active
        if (isOneTimeSupplementPurchased && shipping && discountCode.length && isDiscountActive) {
          discountUrl = `?discount=${discountCode}`
        }
      }

      // Check if the discount price product is selected
      const isDiscountItemSelected = cart.filter(item => item.productId === discountProductPriceId)

      // If there is a supplement and a discount item selected, apply the discount
      let discountValue =
        discountProductTypeSelected.length && isDiscountItemSelected.length ? isDiscountItemSelected[0].price : 0
      setDiscount(discountValue)
    }
    checkoutUrl += `${discountUrl.length > 0 ? discountUrl : ""}`

    returnToUrl += checkoutUrl

    apiUrl = storeUrl + encodeURIComponent(returnToUrl)

    setCheckoutUrl(apiUrl)
  }, [cart, isDiscountActive])

  // Calculate Total and Subtotal in Order Summary
  useEffect(() => {
    let total = 0

    if (cart) {
      cart.forEach(item => {
        if (item && item.productId !== giftId) {
          total += item.quantity * Number(item.price)
        }
      })
    }

    setSubtotal(total)
    setTotal(total - discount)
  }, [cart, discount])

  /** -----------------------------------------
   Shopify Actions
    - addVariantToCart - add product to cart or update it
    - cart useEffect - update or reset cart when change occurs
    - setNewItemInCart - add new item in cart
    - updateQuantity - update product quantity
    - updateItemInCart - updated cart item
    - removeLineItemInCart - remove cart item
    - removeLSItem - remove cart item from local storage
   ------------------------------------------- */

  // Add product to cart or update it
  const addVariantToCart = (
    variantId,
    productId,
    quantity,
    price,
    sellingPlan,
    title,
    type,
    options = {},
    currentCartItem = {},
    isCart,
  ) => {
    const productNewValues = {
      variantId: variantId,
      productId: productId,
      quantity: quantity,
      price: price,
      selling_plan: sellingPlan,
      title: title,
      type: type,
      options: options,
    }

    if (!cart) return

    // If the selected product is the gift id, limit to one variation per product
    if (productId === giftId) {
      // If there is a gift
      let giftInCart = cart.filter(product => product.productId === giftId)
      if (giftInCart.length) {
        updateItemInCart(giftInCart[0], productNewValues, isCart)
      } else {
        setNewItemInCart(variantId, productId, quantity, price, sellingPlan, title, type, options, isCart)
      }
      return
    }

    // If the selected product is the individual offer, limit to one variation per product
    if (offerId.includes(productId)) {
      // If there is a gift
      let offerInCart = cart.filter(product => offerId.includes(product.productId) && productId === product.productId)
      if (offerInCart.length && offerInCart[0].productId === productId) {
        updateItemInCart(offerInCart[0], productNewValues, isCart)
      } else {
        setNewItemInCart(variantId, productId, quantity, price, sellingPlan, title, type, options, isCart)
      }
      return
    }

    // if the request comes from the cart items on cart page, update the currentCartItem with the new values
    if (!isObjectEmpty(currentCartItem)) {
      updateItemInCart(currentCartItem, productNewValues, isCart)
      return
    }

    // Check if the product id is in the cart
    const productIdInCart = cart.filter(product => product.productId === productId)

    // If the productId is not in the cart, add the new product
    if (!productIdInCart.length) {
      setNewItemInCart(variantId, productId, quantity, price, sellingPlan, title, type, options, isCart)
      return
    }

    // If the productId is in the cart
    if (productIdInCart.length) {
      // If the variantId exists in the product objects, update that product with the new values
      const variantIdInProductItems = productIdInCart.filter(product => product.variantId === variantId)
      if (variantIdInProductItems.length) {
        let cartItem

        // If it's supplement or bundle and has the same selling plan update it, else add new product
        if (type === "supplements" || type === "bundles") {
          const productSameSellingPlan = variantIdInProductItems.filter(variant => variant.selling_plan === sellingPlan)

          if (productSameSellingPlan.length) {
            cartItem = productSameSellingPlan[0]
            updateItemInCart(cartItem, productNewValues, isCart)
          } else {
            setNewItemInCart(variantId, productId, quantity, price, sellingPlan, title, type, options, isCart)
          }
        } else {
          cartItem = variantIdInProductItems[0]
          updateItemInCart(cartItem, productNewValues, isCart)
        }
      } else {
        setNewItemInCart(variantId, productId, quantity, price, sellingPlan, title, type, options, isCart)
      }
    }

    if (!isCart) setSidebarOpen(true)

    // push custom event to GTM
    if (typeof window !== "undefined" && window.dataLayer) {
      try {
        window.dataLayer.push({ ecommerce: null })
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: "Google Analytics ThemeContext Ecommerce Tracking",
          },
        })
      }

      try {
        window.dataLayer.push({
          event: "add_to_cart",
          ecommerce: {
            currency: "USD",
            value: 0.0,
            items: [
              {
                item_id: convertVariantIdToStorefrontId(shopifyData, variantId),
                item_name: title,
                currency: "USD",
                item_brand:
                  type === "supplements" || type === "bundles"
                    ? "Whole Betty"
                    : type === "apparel" || type === "merchandise"
                      ? "Betty Rocker"
                      : "The Betty Rocker",
                item_category: type,
                price: +price,
                quantity: quantity,
              },
            ],
          },
        })
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: "Google Analytics add_to_cart Tracking",
            item_category: type,
            item_name: title,
          },
        })
      }
    }
  }

  // Update or reset cart when change occurs
  useEffect(() => {
    if (!cart) return

    if (!cart.length) {
      // reset gift box if cart is empty
      if (giftId.length) setShowGiftBox(false)
      return
    }

    // Reset cart if there are items with old IDs (containing shopify)
    if (
      cart[0].variantId.indexOf("shopify") === -1 ||
      cart[0].productId.indexOf("shopify") === -1 ||
      !cart[0].priceUpdate
    ) {
      setCart([])
      localStorage.setItem("cartItems", JSON.stringify([]))
      setPageLoader(true)
      window.location.reload()
    } else {
      localStorage.setItem("cartItems", JSON.stringify(cart))
    }

    // Updated gift box on cart changes
    if (!giftId.length) return
    const giftInCart = cart.filter(item => item.productId === giftId)
    const giftConditionProductSelected = cart.filter(item => item.type === "supplements" || item.type === "bundles")

    // Remove gift if no supplement or bundle is selected
    if (giftConditionProductSelected.length === 0 && giftInCart.length > 0) {
      let filteredCart = [...cart]
      filteredCart = removeLSItem(filteredCart, giftInCart[0])
      setCart(filteredCart)
    }

    // Show gift box if:
    // - If the product is not in cart
    // - If there is a supplement or bundle selected in cart
    let declinedGift = getCookie("declined-gift")
    if (!declinedGift && giftId.length) {
      setShowGiftBox(giftInCart.length <= 0 && giftConditionProductSelected.length > 0)
    }
  }, [cart])

  // Add new item in cart
  const setNewItemInCart = (variantId, productId, quantity, price, sellingPlan, title, type, options = {}, isCart) => {
    const newCart = [
      ...cart,
      {
        variantId: variantId,
        productId: productId,
        quantity: Number(quantity),
        price: parseFloat(formatPrice(price, 2).replace("$", "")),
        selling_plan: sellingPlan ? sellingPlan : "No",
        title: title,
        type: type,
        options: options,
        priceUpdate: true,
      },
    ]

    // Set unique Id for all the products in the cart
    newCart.forEach((product, index) => {
      product.id = index
    })

    setCart(newCart)

    if (!isCart) {
      setSidebarOpen(true)
    } else {
      window.scrollTo({
        left: 0,
        top: 0,
        behavior: "smooth",
      })
    }
  }

  // Update product quantity
  const updateQuantity = (cartItem, quantity) => {
    const newCart = Array.from(cart)
    const itemInCart = newCart.filter(product => product.id === cartItem.id)

    if (itemInCart.length) {
      itemInCart[0].quantity = quantity
    }

    // If quantity is removed, remove the cart item
    if (quantity === 0) {
      removeLineItemInCart(cartItem)
    } else {
      setCart(newCart)
    }
  }

  // Updated cart item
  const updateItemInCart = (cartItem, productNewValues, isCart) => {
    // Find the product to be updated
    const newCart = [...cart]

    let productToBeUpdated
    // If the product is type supplement or bundles, select the one with same productId, variantId and sellingPlan
    // else select the one with same productId
    if (cartItem.type === "supplements" || cartItem.type === "bundles") {
      productToBeUpdated = newCart.filter(
        product => product.id === cartItem.id && product.selling_plan === cartItem.selling_plan,
      )[0]
    } else {
      productToBeUpdated = newCart.filter(product => product.id === cartItem.id)[0]
    }

    // There is a restriction for products of the type of meal plan and workout plan and Home Workout Domination Protein Bundle. The quantity is limited to 1,
    // so it cannot increase if the product is updated
    const quantity =
      !isCart &&
      cartItem.type !== "meal plan" &&
      cartItem.type !== "workout plan" &&
      !offerId.includes(productNewValues.productId) &&
      productNewValues.productId !== giftId
        ? productNewValues.quantity + productToBeUpdated.quantity
        : productNewValues.quantity

    newCart.forEach(product => {
      if (product.id === productToBeUpdated.id) {
        product.id = cartItem.id
        product.variantId = productNewValues.variantId
        product.productId = productNewValues.productId
        product.quantity = quantity
        product.price = parseFloat(formatPrice(productNewValues.price, 2).replace("$", ""))
        product.selling_plan = productNewValues.selling_plan
        product.title = productNewValues.title
        product.type = productNewValues.type
        product.options = productNewValues.options
      }
    })

    if (productNewValues.quantity === 0) {
      removeLineItemInCart(productToBeUpdated)
    } else {
      // if there are multiple same products, combine them
      // - if they are supplements or bundles combine them only if they have same selling plan
      let sameProductsInCart
      if (productToBeUpdated.type === "supplements" || productToBeUpdated.type === "bundles") {
        sameProductsInCart = newCart.filter(
          product =>
            product.productId === productNewValues.productId &&
            product.variantId === productNewValues.variantId &&
            product.selling_plan === productNewValues.selling_plan,
        )
      } else {
        sameProductsInCart = newCart.filter(
          product =>
            product.productId === productNewValues.productId && product.variantId === productNewValues.variantId,
        )
      }

      if (sameProductsInCart.length > 1) {
        // Calculate the combined quantity
        let quantity = 0
        // Extract the cart item id's which are basically the order of the cart items
        let productIds = []

        sameProductsInCart.forEach((product, index) => {
          // Prevent quantity increase when merging products
          quantity += product.quantity
          productIds = [...productIds, product.id]

          // If it's not the first item, remove the repeating cart item
          if (index !== 0) {
            newCart.splice(product.id, 1)
          }
        })

        // Update the remaining item with the combined quantity
        newCart[productIds[0]].quantity = quantity
      }

      // Reset the ids
      newCart.forEach((product, index) => (product.id = index))

      // Update the cart with the new state
      setCart(newCart)
    }

    if (!isCart) {
      setSidebarOpen(true)
    }
  }

  // Remove product from cart
  const removeLineItemInCart = cartItem => {
    // data for custom event to GTM
    const cartItemId = cartItem.id
    const variantId = cartItem.variantId
    const type = cartItem.type
    const price = parseFloat(formatPrice(cartItem.price, 2).replace("$", ""))
    const quantity = cartItem.quantity
    const title = cartItem.title

    let newCart = Array.from(cart)
    newCart.splice(cartItemId, 1)
    // Update the cart item's id to start from 0
    newCart.forEach((item, index) => {
      item.id = index
    })
    setCart(newCart)
    localStorage.setItem("cartItems", JSON.stringify(newCart))

    // Push custom event to GTM
    if (typeof window !== "undefined" && window.dataLayer) {
      try {
        window.dataLayer.push({ ecommerce: null })
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: "Google Analytics remove_from_cart Ecommerce Tracking",
          },
        })
      }
      try {
        window.dataLayer.push({
          event: "remove_from_cart",
          ecommerce: {
            currency: "USD",
            value: 0.0,
            items: [
              {
                item_id: convertVariantIdToStorefrontId(variantId),
                item_name: title,
                currency: "USD",
                item_brand:
                  type === "supplements" || type === "bundles"
                    ? "Whole Betty"
                    : type === "apparel" || type === "merchandise"
                      ? "Betty Rocker"
                      : "The Betty Rocker",
                item_category: type,
                price: +price,
                quantity: quantity,
              },
            ],
          },
        })
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: "Google Analytics remove_from_cart Tracking",
            item_name: title,
            item_category: type,
          },
        })
      }
    }
  }

  // Remove item from localStorage and update the ids of the rest of the items
  const removeLSItem = (cart, cartItem) => {
    const indexOfItemToBeRemoved = cart.indexOf(cartItem)

    cart.splice(indexOfItemToBeRemoved, 1)
    cart.forEach((cartItem, index) => {
      cartItem.id = index
    })

    return cart
  }

  /** -----------------------------------------
   SHOPIFY PRODUCT ERROR
    - checkCartItemsQuantityStock - Check if the quantity of the cartItems exceeds their stock an update error message
    - findExceedingStockItems - Check if selected products are out of stock
   ------------------------------------------- */

  // Is there shopify error
  const [error, setError] = useState({ status: "", message: "" })

  // Check if the quantity of the cartItems exceeds their stock an update error message
  const checkCartItemsQuantityStock = (shopifyError, cartItems) => {
    let message

    if (shopifyError === "exceeds-stock") {
      const products = findExceedingStockItems(cartItems)

      message = "<ul class='list list--disc'>"

      if (products.length) {
        products.forEach(product => {
          message += `<li>You've requested more of ${product.name} than the available ${product.stock} from this product.</li>`
        })
        message += "</ul>"
      } else {
        message = "We were not able to process your order at this time. Please try again later."
      }
    } else {
      message = "We were not able to process your order at this time. Please try again later."
    }

    setError({ status: shopifyError, message: message })
  }

  // Check if selected products are out of stock
  const findExceedingStockItems = cartItems => {
    let productsExceedingStock = []

    cartItems?.forEach(cartItem => {
      const shopifyProduct = shopifyData.filter(product => product.shopifyId === cartItem.productId)[0]

      if (shopifyProduct.tracksInventory) {
        const shopifyVariant = shopifyProduct.variants.filter(variant => variant.shopifyId === cartItem.variantId)[0]

        if (shopifyVariant.inventoryQuantity < cartItem.quantity) {
          productsExceedingStock = [
            ...productsExceedingStock,
            {
              name: shopifyProduct.title,
              stock: shopifyVariant.inventoryQuantity,
            },
          ]
        }
      }
    })

    return productsExceedingStock
  }

  /** -----------------------------------------
   COOKIES
    - getCookie - get cookie value
    - handleGiftBox - set cookie value if gift has been declined and hide gift box
   ------------------------------------------- */

  // Get cookie value
  function getCookie(name) {
    let value = `; ${document.cookie}`
    let parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(";").shift()
  }

  // Set cookie value if gift has been declined and hide gift box
  const handleGiftBox = () => {
    document.cookie = `declined-gift=true; max-age=${24 * 60 * 60}`
    setShowGiftBox(false)
  }

  /** -----------------------------------------
   SHIPPING
    - updateCountry - Change country USA/UK/CAN - Worldwide
    - handleChangeModalCountry - open/close change country modal
    - country useEffect - if worldwide is selected, check if there are shipping products selected, redirect to homepage
    - removeShippingProductFromCart - if worldwide is selected, check if there are shipping products selected, remove them from Cart
   ------------------------------------------- */

  // Change country USA/UK/CAN - Worldwide
  const updateCountry = (newCountry, isLocalStorage = false) => {
    setCountry(newCountry)

    if (country !== newCountry || isLocalStorage) {
      window.localStorage.setItem("country", newCountry)
      window.localStorage.setItem("shipping", newCountry !== "worldwide")
      setShipping(newCountry !== "worldwide")
    }

    setChangeModalCountry(false)
    // Set storeOverride cookie
    document.cookie = `storeOverride=true; path=/; max-age=31536000` // 1 year
  }

  // Open/close change country modal
  const handleChangeModalCountry = isPopup => setChangeModalCountry(isPopup)

  // If worldwide is selected, check if there are shipping products selected, redirect to homepage
  useEffect(() => {
    if (!country || !cart) return
    setPageLoader(false)

    const notShippingPages = [
      "/",
      "/category/workout-plans/",
      "/category/meal-plans/",
      "/category/programs/",
      "/products/perimenofit/",
      "/products/menofit/",
      "/products/90-day-challenge/",
      "/products/home-workout-domination/",
      "/products/home-workout-domination-2/",
      "/products/30-day-booty-abs-challenge/",
      "/products/lioness/",
      "/products/my-dinner-plan/",
      "/products/body-fuel-system/",
      "/products/30-day-meal-plan/",
      "/products/7-day-meal-plan/",
      "/cart/",
    ]

    if (country === "worldwide") {
      // If not on homepage or workout/meal plan, redirect to homepage
      if (!notShippingPages.includes(window.location.pathname)) {
        navigate("/")
      }
      removeShippingProductFromCart()
    }
  }, [country, cart])

  // If worldwide is selected, check if there are shipping products selected, remove them from Cart
  const removeShippingProductFromCart = () => {
    let filteredCart = [...cart]

    cart.forEach(cartItem => {
      if (cartItem.type !== "meal plan" && cartItem.type !== "workout plan") {
        filteredCart = removeLSItem(filteredCart, cartItem)
      }
    })

    setCart(filteredCart)
  }

  /** -----------------------------------------
   SIDEBAR OPEN/CLOSE
   ------------------------------------------- */
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const handleSidebar = () => setSidebarOpen(!sidebarOpen)

  useEffect(() => {
    if (sidebarOpen) {
      isBrowser && document.body.classList.add("overflow--hidden")
    } else {
      isBrowser && document.body.classList.remove("overflow--hidden")
    }
  }, [sidebarOpen])

  return (
    <ThemeContext.Provider
      value={{
        addVariantToCart: (
          variantId,
          productId,
          quantity,
          price,
          sellingPlan,
          title,
          type,
          options,
          currentCartItem,
          isCart,
        ) =>
          addVariantToCart(
            variantId,
            productId,
            quantity,
            price,
            sellingPlan,
            title,
            type,
            options,
            currentCartItem,
            isCart,
          ),
        updateItemInCart: (
          variantId,
          productId,
          quantity,
          selling_plan,
          price,
          isSidebar,
          currentCartItem,
          options,
          isCart,
        ) =>
          updateItemInCart(
            variantId,
            productId,
            quantity,
            selling_plan,
            price,
            isSidebar,
            currentCartItem,
            options,
            isCart,
          ),
        updateQuantity: (cartItem, quantity) => updateQuantity(cartItem, quantity),
        removeLineItemInCart: cartItem => removeLineItemInCart(cartItem),
        sidebarOpen: sidebarOpen,
        handleSidebar: handleSidebar,
        checkoutUrl: checkoutUrl,
        total: total,
        subtotal: subtotal,
        cart: cart,
        country: country,
        shipping: shipping,
        updateCountry: newCountry => updateCountry(newCountry),
        changeModalCountry: changeModalCountry,
        handleChangeModalCountry: isPopup => handleChangeModalCountry(isPopup),
        pageLoader: pageLoader,
        discount: discount,
        error: error,
        shopifyData: shopifyData,
        giftId: giftId,
        showGiftBox: showGiftBox,
        handleGiftBox: handleGiftBox,
        isDiscountActive: isDiscountActive,
        discountAppliedToBundles: discountAppliedToBundles,
        discountCode: discountCode,
        discountThroughText: discountThroughText,
        discountPercentage: discountPercentage,
        startDateTimeBasedDiscount: startDateTimeBasedDiscount,
        endDateTimeBasedDiscount: endDateTimeBasedDiscount,
        isPromo: isPromo,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
export default ThemeContext

export { ThemeProvider }
